import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, HStack, IconButton, Skeleton, Text, VStack } from "@chakra-ui/react";
import { HiArrowRight, HiStar } from "react-icons/hi2";
import { CampaignService } from "@/services/CampaignService";
import { fromApiResponse } from "@/services/campaignMapper";
import CampaignFormModal from "@/components/campaign/CampaignFormModal";
import { CampaignStatusBadge } from "./components/CampaignStatusBadge";
import CampaignChatLinkButton from "./components/CampaignChatLinkButton";
import { toaster } from "@/components/ui/toaster";

/**
 * Campaign : Workflow equivalent of the Recruitments block in ScenarioDetailPage.jsx --
 * rendered instead of it when the agent is of type SalesManager. See ScenarioDetailPage.jsx.
 */
const isDraftOrInactive = (status) => ["draft", "inactive"].includes(status?.toLowerCase());

const CampaignsSectionSkeleton = () => (
    <VStack w="full" align="flex-start" gap={3}>
        <HStack w="full" justify="space-between">
            <Skeleton height="22px" width="160px" borderRadius={6} />
            <Skeleton height="38px" width="150px" borderRadius={8} />
        </HStack>
        <Box w="full" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
            {Array.from({ length: 4 }).map((_, i) => (
                <HStack key={i} justify="space-between" px={5} py={4} borderBottom={i < 3 ? "1px solid" : "none"} borderColor="gray.200">
                    <Skeleton height="14px" width="180px" borderRadius={4} />
                    <Skeleton height="20px" width="80px" borderRadius="999px" />
                    <Skeleton height="14px" width="90px" borderRadius={4} />
                    <Skeleton height="30px" width="120px" borderRadius={8} />
                </HStack>
            ))}
        </Box>
    </VStack>
);

const CampaignsSection = ({ agentId, agentName, scenarioId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Pre-fetched by NewCampaignModal.jsx right before navigating here, so the create
    // form can autoOpen instantly instead of waiting behind this section's own fetch.
    const prefetchedCampaignsRef = useRef(location.state?.prefetchedCampaigns ?? null);
    const isFirstRun = useRef(true);

    const [campaigns, setCampaigns] = useState(prefetchedCampaignsRef.current ?? []);
    const [isLoading, setIsLoading] = useState(prefetchedCampaignsRef.current === null);
    const [formModal, setFormModal] = useState(null);
    const [favLoadingId, setFavLoadingId] = useState(null);
    const [endingId, setEndingId] = useState(null);

    const fetchCampaigns = () =>
        CampaignService.getByScenario(scenarioId).then((data) => setCampaigns(data?.items ?? []));

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            if (prefetchedCampaignsRef.current !== null) {
                setIsLoading(false);
                return;
            }
        }
        setIsLoading(true);
        fetchCampaigns().finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenarioId]);

    // Lets the "Nowa kampania" picker on the sales dashboard land straight on this
    // scenario's create form (mirrors ScenarioDetailPage's own autoOpenCreate handling
    // for recruitments -- that one only opens JobOfferFormModal, which isn't rendered
    // for a SalesManager agent, so this section needs its own copy of the same flag read).
    useEffect(() => {
        if (location.state?.autoOpenCreate) {
            setFormModal({ mode: "create" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleToggleFavorite = async (id) => {
        setFavLoadingId(id);
        const updated = await CampaignService.toggleFavorite(id);
        setCampaigns((prev) => prev.map((c) => c.id === id ? { ...c, isFavorite: updated.isFavorite } : c));
        setFavLoadingId(null);
    };

    const handleEndCampaign = async (id) => {
        setEndingId(id);
        try {
            await CampaignService.changeStatus(id, "Inactive");
            setCampaigns((prev) => prev.map((c) => c.id === id ? { ...c, status: "Inactive" } : c));
        } finally {
            setEndingId(null);
        }
    };

    const handleEditCampaign = async (campaignId) => {
        const data = await CampaignService.getById(campaignId);
        setFormModal({ mode: "edit", campaignId, initialValues: fromApiResponse(data) });
    };

    if (isLoading) return <CampaignsSectionSkeleton />;

    return (
        <VStack w="full" align="flex-start" gap={3}>
            <HStack w="full" justify="space-between">
                <Text fontWeight={700} fontSize="lg" color="gray.900">Lista kampanii</Text>
                <Button variant="primary" onClick={() => setFormModal({ mode: "create" })}>Nowa kampania</Button>
            </HStack>

            {/* Desktop table */}
            <Box display={{ base: 'none', md: 'block' }} w="full" border="1px solid" borderColor="gray.200" borderRadius={12} overflowX="auto">
                <Box as="table" w="full" minW="720px">
                    <Box as="thead">
                        <Box as="tr" bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                            {["Nazwa kampanii", "Status", "Data utworzenia", "Akcje"].map((h, i) => (
                                <Box as="th" key={h} textAlign={i === 3 ? "right" : "left"} color="gray.500" fontWeight={600} fontSize="xs" textTransform="uppercase" letterSpacing="wider" py={3} px={5}>
                                    {h}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box as="tbody">
                        {campaigns.map((campaign) => (
                            <Box
                                as="tr" key={campaign.id}
                                bg="white" borderBottom="1px solid" borderColor="gray.200"
                                _hover={{ bg: "gray.50" }} cursor="pointer" transition="background 0.15s"
                                onClick={() => navigate(`/models/${agentId}/campaigns/${campaign.id}/leads`)}
                            >
                                <Box as="td" py={4} px={5}>
                                    <Text color="gray.900" fontSize="sm" fontWeight={500}>{campaign.name}</Text>
                                </Box>
                                <Box as="td" py={4} px={5}><CampaignStatusBadge status={campaign.status} /></Box>
                                <Box as="td" py={4} px={5}>
                                    <Text color="gray.500" fontSize="sm">{new Date(campaign.createdAtUtc).toLocaleDateString('pl-PL')}</Text>
                                </Box>
                                <Box as="td" py={4} px={5} textAlign="right">
                                    <HStack gap={2} justify="flex-end">
                                        <IconButton
                                            variant="ghost" size="sm" aria-label="Ulubione"
                                            color={campaign.isFavorite ? "yellow.400" : "gray.500"}
                                            loading={favLoadingId === campaign.id}
                                            onClick={(e) => { e.stopPropagation(); handleToggleFavorite(campaign.id); }}
                                        >
                                            <HiStar />
                                        </IconButton>
                                        {!isDraftOrInactive(campaign.status) &&
                                            <Button
                                                variant="outlined" size="sm" color="red.500" borderColor="red.200" _hover={{ bg: "red.50" }}
                                                loading={endingId === campaign.id}
                                                onClick={(e) => { e.stopPropagation(); handleEndCampaign(campaign.id); }}
                                            >
                                                Zakończ
                                            </Button>
                                        }
                                        <CampaignChatLinkButton chatPath={campaign.chatPath} />
                                        <Button variant="outlined" size="sm" onClick={(e) => { e.stopPropagation(); handleEditCampaign(campaign.id); }}>
                                            Edytuj
                                        </Button>
                                        <IconButton
                                            variant="outlined" size="sm" aria-label="Leady kampanii"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/models/${agentId}/campaigns/${campaign.id}/leads`); }}
                                        >
                                            <HiArrowRight />
                                        </IconButton>
                                    </HStack>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Mobile card list */}
            <VStack display={{ base: 'flex', md: 'none' }} w="full" gap={2}>
                {campaigns.map((campaign) => (
                    <Box
                        key={campaign.id} w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={10} p={4}
                        cursor="pointer" onClick={() => navigate(`/models/${agentId}/campaigns/${campaign.id}/leads`)}
                    >
                        <HStack justify="space-between" align="flex-start" mb={2}>
                            <VStack align="flex-start" gap={1} flex={1} minW={0}>
                                <Text color="gray.900" fontSize="sm" fontWeight={600}>{campaign.name}</Text>
                                <CampaignStatusBadge status={campaign.status} />
                            </VStack>
                            <IconButton
                                variant="ghost" size="sm" aria-label="Ulubione"
                                color={campaign.isFavorite ? "yellow.400" : "gray.500"}
                                loading={favLoadingId === campaign.id}
                                onClick={(e) => { e.stopPropagation(); handleToggleFavorite(campaign.id); }}
                            >
                                <HiStar />
                            </IconButton>
                        </HStack>
                        <HStack gap={2} justify="flex-end">
                            {!isDraftOrInactive(campaign.status) &&
                                <Button
                                    variant="outlined" size="sm" color="red.500" borderColor="red.200" _hover={{ bg: "red.50" }}
                                    loading={endingId === campaign.id}
                                    onClick={(e) => { e.stopPropagation(); handleEndCampaign(campaign.id); }}
                                >
                                    Zakończ
                                </Button>
                            }
                            <CampaignChatLinkButton chatPath={campaign.chatPath} />
                            <Button variant="outlined" size="sm" onClick={(e) => { e.stopPropagation(); handleEditCampaign(campaign.id); }}>Edytuj</Button>
                        </HStack>
                    </Box>
                ))}
            </VStack>

            {formModal && (
                <CampaignFormModal
                    agentName={agentName}
                    scenarioId={scenarioId}
                    initialValues={formModal.initialValues}
                    onClose={() => setFormModal(null)}
                    onSubmit={(values) => {
                        const action = formModal.mode === "create"
                            ? CampaignService.create(scenarioId, values)
                            : CampaignService.update(formModal.campaignId, values);
                        return action.then(() => {
                            toaster.create({ type: "success", title: "Kampania zapisana" });
                            fetchCampaigns();
                            setFormModal(null);
                        });
                    }}
                />
            )}
        </VStack>
    );
};

export default CampaignsSection;
